import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Link2,
  Link2Off,
  Loader2,
  Mail,
  Phone,
  Trash2,
  UserPlus,
  Users2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useAssignEnquiry,
  useDeleteEnquiry,
  useEnquiry,
  useMarkSpam,
  useUpdateEnquiry,
} from "../hooks/useEnquiries";
import { ConvertToContactModal } from "../components/ConvertToContactModal";
import {
  EnquiryPriorityBadge,
  EnquiryStatusBadge,
  SourceBadge,
} from "../components/EnquiryStatusBadge";
import { LinkLeadModal } from "@/modules/leads/components/LinkLeadModal";
import { LeadStatusBadge } from "@/modules/leads/components/LeadStatusBadge";
import { useUnlinkEnquiryFromLead } from "@/modules/leads/hooks/useLeads";

const CARD_BORDER = "hsl(210,30%,85%)";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

export default function EnquiryDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions?.enquiries;
  const canEdit = !!perms?.canEdit;
  const canDelete = !!perms?.canDelete;

  const { data: enquiry, isLoading, isError } = useEnquiry(id);
  const users = useUserList({ limit: 200 });
  const assign = useAssignEnquiry(id);
  const update = useUpdateEnquiry(id);
  const markSpam = useMarkSpam(id);
  const del = useDeleteEnquiry();

  const [convertOpen, setConvertOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  const [linkLeadOpen, setLinkLeadOpen] = useState(false);
  const unlinkLead = useUnlinkEnquiryFromLead(id);

  const breadcrumb = useMemo(
    () => [
      { label: "PhazeX Hub", href: "/" },
      { label: "CRM" },
      { label: "Enquiries", href: "/enquiries" },
      { label: enquiry?.code ?? "…" },
    ],
    [enquiry?.code],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading enquiry…
        </div>
      </AppLayout>
    );
  }

  if (isError || !enquiry) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="py-20 text-center text-sm text-muted-foreground">
          Enquiry not found.
        </div>
      </AppLayout>
    );
  }

  const isConverted = enquiry.status === "converted" || !!enquiry.linkedContactId;

  return (
    <AppLayout breadcrumb={breadcrumb}>
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/enquiries")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to Enquiries
          </button>
          <div className="flex items-center gap-2">
            {canEdit && !isConverted && (
              <Button onClick={() => setConvertOpen(true)}>
                <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                Convert to Contact
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outline"
                onClick={() =>
                  markSpam.mutate({ isSpam: !enquiry.isSpam })
                }
              >
                {enquiry.isSpam ? "Unmark Spam" : "Mark Spam"}
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                Delete
              </Button>
            )}
          </div>
        </div>

        {enquiry.isSpam && (
          <div className="flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="font-medium">
                Flagged as spam{" "}
                {enquiry.spamScore != null && `(score ${enquiry.spamScore})`}
              </div>
              {enquiry.spamReason && (
                <div className="mt-0.5 text-xs">{enquiry.spamReason}</div>
              )}
            </div>
          </div>
        )}

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-foreground">
                {enquiry.subject ?? "(no subject)"}
              </h1>
              <ReferenceIdBadge id={enquiry.code} />
              <SourceBadge source={enquiry.source} />
              <EnquiryStatusBadge status={enquiry.status} />
              <EnquiryPriorityBadge priority={enquiry.priority} />
            </div>
            {enquiry.linkedContactId && (
              <button
                onClick={() => navigate(`/contacts/${enquiry.linkedContactId}`)}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Linked contact: {enquiry.linkedContactName ?? "View contact"} →
              </button>
            )}
          </CardContent>
        </Card>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Submitter Info
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <Field label="Name" value={enquiry.name} />
              <Field
                label="Email"
                value={
                  enquiry.email ? (
                    <a
                      href={`mailto:${enquiry.email}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Mail className="h-3.5 w-3.5" /> {enquiry.email}
                    </a>
                  ) : null
                }
              />
              <Field
                label="Phone"
                value={
                  enquiry.phone ? (
                    <a
                      href={`tel:${enquiry.phone}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Phone className="h-3.5 w-3.5" /> {enquiry.phone}
                    </a>
                  ) : null
                }
              />
              <Field label="Company" value={enquiry.companyName} />
              <Field label="Job Title" value={enquiry.jobTitle} />
              <Field label="Country" value={enquiry.country} />
            </div>
          </CardContent>
        </Card>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Enquiry Content
            </h3>
            <Field label="Subject" value={enquiry.subject} />
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Message
              </div>
              <div className="whitespace-pre-wrap text-sm text-foreground">
                {enquiry.message || "—"}
              </div>
            </div>
            {enquiry.metadata != null &&
              Object.keys(enquiry.metadata as object).length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowMeta((v) => !v)}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    {showMeta ? "Hide" : "Show"} metadata
                  </button>
                  {showMeta && (
                    <pre className="mt-2 overflow-x-auto rounded-md bg-[hsl(210,30%,97%)] p-3 text-xs">
                      {JSON.stringify(enquiry.metadata, null, 2)}
                    </pre>
                  )}
                </div>
              )}
          </CardContent>
        </Card>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Lead</h3>
              {canEdit && (
                <div className="flex gap-2">
                  {enquiry.leadId ? (
                    <Button
                      variant="outline"
                      onClick={() => unlinkLead.mutate()}
                      disabled={unlinkLead.isPending}
                    >
                      <Link2Off className="h-4 w-4" strokeWidth={1.5} />
                      Unlink
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    onClick={() => setLinkLeadOpen(true)}
                  >
                    <Link2 className="h-4 w-4" strokeWidth={1.5} />
                    {enquiry.leadId ? "Change Lead" : "Link to existing Lead"}
                  </Button>
                </div>
              )}
            </div>
            {enquiry.leadId ? (
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Users2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <button
                  onClick={() => navigate(`/leads/${enquiry.leadId}`)}
                  className="font-mono text-xs text-primary hover:underline"
                >
                  {enquiry.leadCode}
                </button>
                <span className="text-foreground">
                  {enquiry.leadName ?? "(unnamed)"}
                </span>
                {enquiry.leadStatus && (
                  <LeadStatusBadge status={enquiry.leadStatus} />
                )}
                {enquiry.leadEmail && (
                  <span className="text-xs text-muted-foreground">
                    · {enquiry.leadEmail}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Not linked to any Lead.
                {!enquiry.email && !enquiry.phone
                  ? " Auto-linking needs an email or phone on the enquiry — none present."
                  : ""}
              </p>
            )}
          </CardContent>
        </Card>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">Triage</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Assigned To
                </div>
                {canEdit ? (
                  <Select
                    value={enquiry.assignedToId ?? ""}
                    onChange={(e) =>
                      assign.mutate(e.target.value || null)
                    }
                    className="w-60"
                  >
                    <option value="">— Unassigned —</option>
                    {users.data?.data.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="text-sm">{enquiry.assignedToName ?? "—"}</div>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Status
                </div>
                {canEdit ? (
                  <Select
                    value={enquiry.status}
                    onChange={(e) =>
                      update.mutate({ status: e.target.value })
                    }
                    className="w-60"
                  >
                    {["new", "in_progress", "qualified", "converted", "closed", "spam"].map(
                      (s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ),
                    )}
                  </Select>
                ) : (
                  <EnquiryStatusBadge status={enquiry.status} />
                )}
              </div>
              <Field label="Category" value={enquiry.category} />
              <Field
                label="Linked Company"
                value={enquiry.linkedCompanyName}
              />
              <Field
                label="Linked Product"
                value={enquiry.linkedProductName}
              />
              <Field
                label="Linked Project"
                value={enquiry.linkedProjectName}
              />
            </div>
          </CardContent>
        </Card>

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Response Timeline
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <Field
                label="First Response"
                value={
                  enquiry.firstResponseAt
                    ? new Date(enquiry.firstResponseAt).toLocaleString("en-GB")
                    : null
                }
              />
              <Field
                label="Resolved"
                value={
                  enquiry.resolvedAt
                    ? new Date(enquiry.resolvedAt).toLocaleString("en-GB")
                    : null
                }
              />
              <Field
                label="Received"
                value={new Date(enquiry.createdAt).toLocaleString("en-GB")}
              />
              <Field label="Source URL" value={enquiry.sourceUrl} />
            </div>
          </CardContent>
        </Card>

        <ConvertToContactModal
          enquiry={enquiry}
          open={convertOpen}
          onOpenChange={setConvertOpen}
        />

        <LinkLeadModal
          enquiryId={enquiry.id}
          enquiryEmail={enquiry.email}
          enquiryPhone={enquiry.phone}
          enquiryName={enquiry.name}
          open={linkLeadOpen}
          onOpenChange={setLinkLeadOpen}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete enquiry?"
          description={`Delete ${enquiry.code}? It will be removed from the inbox.`}
          onConfirm={async () => {
            await del.mutateAsync(enquiry.id);
            navigate("/enquiries");
          }}
        />
      </div>
    </AppLayout>
  );
}
